import { postConversationMetadata } from 'api/v2/AI';
import {
  aiAdvisorMonthlyLimitLockContent,
  aiAdvisorPageLock,
} from 'components/organisms/PaywallLockV2/locks.content';
import useFeatureLock from 'hooks/Subscriptions/useFeatureLock';
import _isEqual from 'lodash/isEqual';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import type {
  ConversationMetadata,
  ConversationMetadataType,
} from 'types/ai.types';
import {
  SubscriptionCounterKeysEnum,
  SubscriptionLockKeysEnum,
} from 'types/subscriptions.types';
import { SentryIgnoredError } from 'utils/SentryIgnoredError';

type AIMetadataContextType = {
  toastOffset: number;
  setToastOffset: (toastOffset: number) => void;
  metadata: ConversationMetadata | null;
  metadataItems: MetadataItemMap;
  setMetadata: (
    metadata: ConversationMetadata | null,
    type: ConversationMetadataType,
  ) => void;
  clearMetadata: () => void;
  isHelperOpen: boolean;
  setIsHelperOpen: (isHelperOpen: boolean) => void;
};

export const AIMetadataContext = createContext<AIMetadataContextType>({
  toastOffset: 0,
  setToastOffset: () => {},
  metadata: null,
  metadataItems: {},
  setMetadata: () => {},
  clearMetadata: () => {},
  isHelperOpen: false,
  setIsHelperOpen: () => {},
});

type AIMetadataProviderProps = {
  children: React.ReactNode;
};

type MetadataItem = {
  metadata: ConversationMetadata | null;
  timestamp: number;
};

type MetadataItemMap = Partial<Record<ConversationMetadataType, MetadataItem>>;

export const AIMetadataProvider: FC<AIMetadataProviderProps> = ({
  children,
}) => {
  const [toastOffset, setToastOffset] = useState(0);
  const [metadataItems, setMetadataItems] = useState<MetadataItemMap>({});
  const [isHelperOpen, setIsHelperOpen] = useState(false);

  const { withPaywallLock, withPaywallCounterLock } = useFeatureLock();

  const { metadata } = useMemo(
    () =>
      Object.values(metadataItems).reduce(
        (acc, { metadata, timestamp }) => {
          if (!metadata) return acc;
          if (!acc || timestamp > acc.timestamp) return { metadata, timestamp };
          return acc;
        },
        { metadata: null, timestamp: 0 },
      ),
    [metadataItems],
  );

  const setMetadata = useCallback(
    (
      newMetadata: ConversationMetadata | null,
      type: ConversationMetadataType,
    ) =>
      setMetadataItems((prev) => ({
        ...prev,
        [type]: { metadata: newMetadata, timestamp: Date.now() },
      })),
    [setMetadataItems],
  );

  const clearMetadata = useCallback(() => setMetadataItems({}), []);

  const _setIsHelperOpen = useCallback(
    (newIsHelperOpen: boolean) => {
      // Only apply paywall checks when opening the helper, not when closing
      if (newIsHelperOpen) {
        withPaywallLock(
          () =>
            withPaywallCounterLock(
              () => setIsHelperOpen(newIsHelperOpen),
              SubscriptionCounterKeysEnum.taxAdvisorMonthlyMessages,
              aiAdvisorMonthlyLimitLockContent,
            ),
          SubscriptionLockKeysEnum.viewWebAIAdvisorPage,
          aiAdvisorPageLock as any,
        );
      } else {
        setIsHelperOpen(false);
      }
    },
    [withPaywallCounterLock, withPaywallLock],
  );

  const value: AIMetadataContextType = {
    toastOffset,
    setToastOffset,
    metadataItems,
    metadata,
    setMetadata,
    clearMetadata,
    isHelperOpen,
    setIsHelperOpen: _setIsHelperOpen,
  };

  return (
    <AIMetadataContext.Provider value={value}>
      {children}
    </AIMetadataContext.Provider>
  );
};

export const useAiMetadata = (): AIMetadataContextType => {
  const context = useContext(AIMetadataContext);

  if (!context) {
    throw new SentryIgnoredError(
      'useAiMetadata must be used within a AIMetadataProvider',
    );
  }

  return context;
};

type MetadataMapper<T> = (data: T) => any;

export function useSetAiMetadata<T = any>(
  newMetadata: T | undefined,
  type: ConversationMetadataType,
  mapMetadata?: MetadataMapper<T>,
): void {
  const { setMetadata } = useAiMetadata();
  const currentMetadataRef = useRef<ConversationMetadata | null>(null);

  useEffect(() => {
    const parsedMetadata =
      mapMetadata && newMetadata
        ? mapMetadata(newMetadata)
        : newMetadata || null;

    if (!_isEqual(parsedMetadata, currentMetadataRef.current)) {
      setMetadata(parsedMetadata, type);
      currentMetadataRef.current = parsedMetadata;
    }
  }, [newMetadata, type, mapMetadata, setMetadata]);

  // Separate effect for cleanup
  useEffect(() => {
    return () => {
      currentMetadataRef.current && setMetadata(null, type);
    };
  }, [type, setMetadata]);
}

export const useSetMetadataDocumentId = () => {
  const { metadata } = useAiMetadata();

  return (documentId: string) =>
    metadata &&
    'data' in metadata &&
    postConversationMetadata({ ...metadata, documentId });
};
